import React from "react";

/**
 * Shared Practice Team action-menu rhythm for member-level overflow actions.
 * Consumers keep their own action semantics while sharing the same geometry.
 */
export default function MemberActionMenu({
  items = [],
  open = false,
  onToggle,
  onRequestClose,
  label,
  triggerIcon,
  className = "",
  menuTestId,
}) {
  const rootRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsidePointer = (event) => {
      if (!rootRef.current?.contains(event.target)) onRequestClose?.();
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onRequestClose?.();
    };
    document.addEventListener("mousedown", closeOnOutsidePointer);
    document.addEventListener("touchstart", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsidePointer);
      document.removeEventListener("touchstart", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [onRequestClose, open]);

  if (!items.length) return null;

  return (
    <div
      ref={rootRef}
      className={["relative", className].filter(Boolean).join(" ")}
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="inline-flex h-10 w-10 items-center justify-center rounded-[14px] bg-[rgba(86,106,139,0.08)] text-on-surface-var transition hover:bg-[rgba(24,50,119,0.08)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(29,78,216,0.22)]"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={onToggle}
      >
        {triggerIcon}
      </button>
      {open ? (
        <div
          className="absolute right-0 top-11 z-[60] w-[200px] overflow-hidden rounded-[12px] border border-[rgba(15,23,42,0.08)] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0px_12px_24px_-10px_rgba(29,79,215,0.10),0px_6px_12px_-4px_rgba(26,27,35,0.08)]"
          data-practice-team-action-menu={menuTestId || ""}
          role="menu"
        >
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              className={[
                "flex w-full items-center gap-3 px-4 py-3 text-left text-body-medium hover:bg-[rgba(24,50,119,0.06)] disabled:cursor-not-allowed disabled:opacity-60",
                item.danger ? "text-[var(--on-critical)]" : "text-on-surface",
              ].join(" ")}
              onClick={() => {
                onRequestClose?.();
                item.onClick?.();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
